import { gettext } from '@eventbrite/i18n';
import loadable from '@loadable/component';
import React from 'react';
import { LazyRenderer } from '../components/LazyRenderer';
import { CategoryBrowseBreadCrumb } from '../containers/CategoryBrowseBreadCrumb';
import { CategoryBrowseDateFilter } from '../containers/CategoryBrowseDateFilter';
import { CategoryBrowseDynamicFormatEvents } from '../containers/CategoryBrowseDynamicFormatEvents';
import { CategoryBrowseEvents } from '../containers/CategoryBrowseEvents';
import { CategoryBrowseHeaderContainer } from '../containers/CategoryBrowseHeaderContainer';
import { CategoryBrowseLocationAutocomplete } from '../containers/CategoryBrowseLocationAutocomplete';
import { CategoryBrowseRelatedCategories } from '../containers/CategoryBrowseRelatedCategories';
import { useEnvContext, useRouteContext } from '../context';
import './CategoryBrowsePage.scss';

const CategoryBrowseOrganizerTags = loadable(
    () => import('../containers/CategoryBrowseOrganizerTags'),
    {
        fallback: <div style={{ minHeight: '200px' }}></div>,
        ssr: false,
    },
);

const CategoryBrowseOrganizers = loadable(
    () => import('../containers/CategoryBrowseOrganizers'),
);

const CategoryBrowseNearbyCities = loadable(
    () => import('../containers/CategoryBrowseNearbyCities'),
);

const CategoryBrowsePopularCategories = loadable(
    () => import('../containers/CategoryBrowsePopularCategories'),
    {
        resolveComponent: (components: {
            CategoryBrowsePopularCategories: React.FunctionComponent;
        }) => components.CategoryBrowsePopularCategories,
    },
);

const CategoryBrowsePopularFormats = loadable(
    () => import('../containers/CategoryBrowsePopularFormats'),
);

const CategoryBrowsePopularTimes = loadable(
    () => import('../containers/CategoryBrowsePopularTimes'),
);

//TODO: Set date filter to leverage current future and date when both applied
export const CategoryBrowsePage: React.FunctionComponent = () => {
    // Env and General Data
    const envContext = useEnvContext();
    const user = envContext?.user;
    const locale = envContext?.env?.localeInfo?.locale || 'en_US';
    const gaSettings = envContext?.gaSettings;
    const isBotRequest = envContext?.isBotRequest;

    //Current Tag and Search Values
    const { parsedValues, location } = useRouteContext();
    const activeTag = parsedValues?.activeTag || '';
    const date = parsedValues?.date;
    const customDate = parsedValues?.customDate;

    return (
        <>
            <CategoryBrowseBreadCrumb />
            <CategoryBrowseHeaderContainer
                activeCategory={activeTag}
                isAuthenticated={!!user?.isAuthenticated}
            />
            <main className="category-browse__main">
                <LazyRenderer numChildInitialRender={isBotRequest ? -1 : 2}>
                    <section className="category-browse__filters">
                        <CategoryBrowseLocationAutocomplete />
                        <CategoryBrowseDateFilter locale={locale} />
                    </section>
                    <CategoryBrowseRelatedCategories />
                    <CategoryBrowseEvents
                        renderEmptyState={true}
                        renderLoading={true}
                        bucketKey="most_popular"
                        bucketPosition="2"
                        includePromotedEvents={true}
                    />
                    {date !== 'current_future' || !!customDate ? null : (
                        <CategoryBrowseEvents
                            renderLoading={true}
                            lazyLoadCarouselElements={true}
                            queryOverload={{
                                page_size: 8,
                                dates: ['this_weekend'],
                                aggs: {},
                            }}
                            title={gettext('This weekend')}
                            bucketKey="this_weekend"
                            bucketPosition="3"
                        />
                    )}
                    <CategoryBrowseOrganizers
                        user={user}
                        gaSettings={gaSettings}
                        location={location}
                    />
                    <CategoryBrowseDynamicFormatEvents
                        bucketNumber={0}
                        bucketPosition="5"
                    />
                    <CategoryBrowseEvents
                        renderLoading={true}
                        lazyLoadCarouselElements={true}
                        forceRenderHeader={isBotRequest}
                        queryOverload={{
                            page_size: 8,
                            price: 'free',
                            aggs: {},
                        }}
                        title={gettext('Free')}
                        bucketKey="free"
                        bucketPosition="6"
                    />
                    <CategoryBrowseOrganizerTags />
                    <CategoryBrowseDynamicFormatEvents
                        bucketNumber={1}
                        bucketPosition="8"
                    />
                    <CategoryBrowseDynamicFormatEvents
                        bucketNumber={2}
                        bucketPosition="9"
                    />
                    <CategoryBrowsePopularFormats />
                    <CategoryBrowsePopularTimes />
                    <CategoryBrowsePopularCategories />
                    <CategoryBrowseNearbyCities />
                </LazyRenderer>
            </main>
        </>
    );
};

export default CategoryBrowsePage;
